<script setup lang="ts">
import { CfgTypography, CfgTypographySizes } from '@cfgtech/ui';
import { ArrowRightIcon, PositiveEmotionIcon, NegativeEmotionIcon, SubtitleIcon } from '@cfgtech/icons';
import { lowerFirst } from 'lodash-es';
import { APP_DATE_FORMAT } from '~/constants/common';
import type { Events } from '~/composables/events/useEvents';
import { SeverityEnum, UpdateTypeEnum } from '~/services/generated/client';

const props = defineProps<{
  event: Events & {
    path: string | null
  }
}>();

const emit = defineEmits<{
  'click': []
}>();

const dataLayer = useDataLayer();

const { t } = useI18n();

const router = useRouter();

const iconMapper = {
  [SeverityEnum.Positive]: {
    icon: PositiveEmotionIcon,
    color: 'text-success'
  },

  [SeverityEnum.Neutral]: {
    icon: SubtitleIcon,
    color: 'text-secondary'
  },

  [SeverityEnum.Negative]: {
    icon: NegativeEmotionIcon,
    color: 'text-red'
  }
};

const icon = computed(() => iconMapper[props.event.severity]);
const eventName = computed(() => props.event.name || props.event?.data?.name);

const eventTitle = computed(() => {
  const key = `events.types.${props.event.type}`;
  const data = props.event.data as unknown as Record<string, any>; // TODO: Specify type on BE

  switch (props.event.type) {
    case UpdateTypeEnum.FinancialResultNew:
      return t(key, { year: data.financial_result?.year });

    case UpdateTypeEnum.ReturnsPaid:
    case UpdateTypeEnum.ReturnsNotPaid:
      return t(key, { period: data.return?.period });

    case UpdateTypeEnum.ReturnsPeriod:
      return t(key, { period: data.period });

    case UpdateTypeEnum.DocumentAdded:
      return t(
        `${key}.${data.fileCategories?.length === 1 ? 'singular' : 'multiple'}`,
        {
          type: (data.fileCategories || []).map(
            ({ name, type }: { name: string, type: string }) => lowerFirst(name || t(`issue.fileCategories.${type}`))
          ).join(' a ')
        }
      );

    case UpdateTypeEnum.CollateralAdded:
    case UpdateTypeEnum.CollateralRemoved:
      return t(key, {
        type: (data.collaterals || []).map(({ type = '' }) => lowerFirst(
          t(`issue.collateralTypes.declensions.instrumental.${type}`)
        )).join(' a ')
      });

    case UpdateTypeEnum.ChangeAddress:
      return t(key, { to: data.new_address });

    case UpdateTypeEnum.ChangeOwner:
      return t(key, { name: data.owner?.name || '' });

    case UpdateTypeEnum.NewIssue:
      return t(key, { name: data.issue?.name });

    case UpdateTypeEnum.ChangeIssueParameter:
      return t(`${key}.${data.params?.length === 1 ? 'singular' : 'multiple'}`, {
        params: (data.params || []).map(
          ({ key: keyName }: { key: string, value: string }) => lowerFirst(t(`issue.${keyName}.title`))
        ).join(', ')
      });

    case UpdateTypeEnum.News:
      return data.title;

    default:
      return t(key);
  }
});

async function handleAnchorClick(event: MouseEvent) {
  const path = (event.target as HTMLAnchorElement).closest('a')?.getAttribute('href');

  const [pathWithoutHash = '', hash = ''] = path?.split('#') || [];

  if (path) {
    emit('click');

    if (router.currentRoute.value.path !== pathWithoutHash) {
      // events card only go to issue detail page
      if (pathWithoutHash.includes('/portfolio')) {
        dataLayer.openIssueDetail(LocationsEnum.EVENTS);
      } else if (pathWithoutHash === '/returns') {
        dataLayer.openReturns(LocationsEnum.EVENTS);
      }
    }

    // If url contains hash, we need to replace it with a new one to trigger hashchange event
    if (router.currentRoute.value.fullPath === path) {
      router.replace(pathWithoutHash!);
    }

    if (path === router.currentRoute.value.path && hash) {
      window.location.hash = hash;
      return;
    }

    await router.push(path);
  }
}
</script>

<template>
  <Component
    :is="event.path ? 'a' : 'div'"
    class="flex justify-between rounded-xl p-3.5 shadow-card"
    v-bind="{
      ...(event.path ? { href: event.path } : {})
    }"
    @click.prevent="handleAnchorClick"
  >
    <div class="flex grow flex-col gap-y-2">
      <CfgTypography>
        {{ eventName }}
      </CfgTypography>

      <div class="flex gap-x-2.5">
        <span v-if="icon?.icon" class="pt-0.5">
          <component
            :is="icon.icon"
            class="text-[1.5em]"
            :class="icon?.color"
          />
        </span>

        <div class="flex flex-col gap-y-1">
          <CfgTypography class="line-clamp-2 !font-semibold text-brand-dark" :size="CfgTypographySizes.md">
            {{ eventTitle }}
          </CfgTypography>

          <CfgTypography class="text-grey-400" :size="CfgTypographySizes.xs">
            {{ t(`events.categories.${event.category}`) }}
          </CfgTypography>
        </div>
      </div>
    </div>

    <div
      class="flex shrink-0 flex-col items-end"
      :class="{
        'justify-between': event.path,
        'justify-end': !event.path
      }"
    >
      <ArrowRightIcon v-if="event.path" class="text-h3 text-brand" />

      <CfgTypography class="!font-semibold text-grey-400" :size="CfgTypographySizes.sm">
        {{ $dayjs(event?.data?.date || event.createdAt).format(APP_DATE_FORMAT) }}
      </CfgTypography>
    </div>
  </Component>
</template>
